
import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { Select } from 'antd';

import { axiosInstance, isCondoWeeklySpeccialMarketingFromPath, RootState, UrlUtils } from '@share/utils';
import { Routes, Urls } from '@share/constants';
import { ICondoWeeksFilters } from '@common-types';
import { APP_SELECTOR } from '@constants';

import { CondoWeeksCard } from '../condo-week-card';
import { CondoWeeksEmpty } from '../condo-weeks-empty';
import { CondoWeeksFilters } from '../condo-week-filters';
import { CondoWeeksCardSkeleton } from '../condo-week-card-skeleton';
import { INITIAL_FITLERS, ProcessFilterParams, ProcessFilterParamsObject } from './utils';

import CondoWeeks from '@assets/images/condo-weeks.jpg';

import './style.scss';

const QUERY_PARAM = 'queryHash';
const REDIRECT_QUERY_PARAM = 'redirect';
const PAGE = 12;
const debounceTime = 300;
const scrollPixels = 450;

interface ISortState {
  orderBy: string;
  sortOrder: 'asc' | 'desc';
}

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'locationname_asc', label: 'Location Name (asc)' },
  { value: 'locationname_desc', label: 'Location Name (desc)' },
  { value: 'propertyname_asc', label: 'Property Name (asc)' },
  { value: 'propertyname_desc', label: 'Property Name (desc)' },
  { value: 'weeks_asc', label: 'Weeks (asc)' },
  { value: 'weeks_desc', label: 'Weeks (desc)' },
  { value: 'roomdescription_asc', label: 'Room Description (asc)' },
  { value: 'roomdescription_desc', label: 'Room Description (desc)' },
  { value: 'checkin_asc', label: 'Check-In (asc)' },
  { value: 'checkin_desc', label: 'Check-In (desc)' },
  { value: 'checkout_asc', label: 'Check-Out (asc)' },
  { value: 'checkout_desc', label: 'Check-Out (desc)' },
  { value: 'supplier_asc', label: 'Supplier (asc)' },
  { value: 'supplier_desc', label: 'Supplier (desc)' },
  { value: 'lengthofstay_asc', label: 'Length of Stay (asc)' },
  { value: 'lengthofstay_desc', label: 'Length of Stay (desc)' },
  { value: 'maxoccupancy_asc', label: 'Max Occupancy (asc)' },
  { value: 'maxoccupancy_desc', label: 'Max Occupancy (desc)' },
  { value: 'bedrooms_asc', label: 'Bedrooms (asc)' },
  { value: 'bedrooms_desc', label: 'Bedrooms (desc)' },
  { value: 'bathrooms_asc', label: 'Bathrooms (asc)' },
  { value: 'bathrooms_desc', label: 'Bathrooms (desc)' },
  { value: 'price_asc', label: 'Price (asc)' },
  { value: 'price_desc', label: 'Price (desc)' },
];

interface IProps {
  isWeeks?: boolean;
  isAccount?: boolean;
  isWeeklySpecials?: boolean;
  isMarketing?: boolean;
  isWeeklySpecialsParam?: boolean;
}

export function CondoWeeksWrapper(props: IProps) {

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [filters, setFilters] = useState({ ...INITIAL_FITLERS });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [locations, setLocations] = useState([]);
  const [sort, setSort] = useState<ISortState | null>(null);

  const currentPageRef = useRef(0);
  const totalOrderCountRef = useRef(0);
  const resultsRef = useRef<any[]>([]);
  const filtersRef = useRef(filters);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);
  const sortRef = useRef<ISortState | null>(null);

  const loginStore = useSelector((state: RootState) => state.loginStore);
  const navigationMenuStore = useSelector((state: RootState) => state.navigationMenuStore);
  const { isWeeks, isAccount, isWeeklySpecials, isMarketing, isWeeklySpecialsParam } = props;
  
  const { items } = navigationMenuStore;
  const { account } = loginStore;

  const root = document.querySelector(APP_SELECTOR) as HTMLDivElement;

  const history = useHistory();

  const weeklySpecialHash = account?.weeklySpecialHash;
  const isWeeklySpecialHash = account?.weeklySpecialHashEnabled && 
  ((!account?.weeklySpecialHashRsiSiteidList?.length && !account?.weeklySpecialHashRsiOrgidList?.length) || 
  (account?.weeklySpecialHashRsiSiteidList?.includes(items?.siteId?.toString()) || account?.weeklySpecialHashRsiOrgidList?.includes(items?.organizationId?.toString())));
  
  const queryHashAccount = weeklySpecialHash?.value;

  const { id: hashParam } = useParams<{ id: string }>();

  const queryHashParam = useMemo(() => UrlUtils.getSearchParam(QUERY_PARAM), []);
  const redirect = useMemo(() => UrlUtils.getSearchParam(REDIRECT_QUERY_PARAM), []);
  
  const isCondoWeeklySpecialMarketing = useMemo(() => isCondoWeeklySpeccialMarketingFromPath(history), [history]);

  const queryHash = isWeeklySpecialsParam ? hashParam : isWeeklySpecials ? queryHashAccount : queryHashParam;

  if (isWeeklySpecialsParam && !hashParam) {
    history.push(Routes.BasicError);
    return null;
  }

  useEffect(() => {
    if (!isCondoWeeklySpecialMarketing &&((!isWeeks && isEmpty(queryHash)) || (isWeeklySpecials && (!isWeeklySpecialHash || isEmpty(weeklySpecialHash))))) {
      history.push(Routes.NotAuthorized);
    }

    setLoading(true);
    setResults([]);
    setCurrentPage(1);

    handleLocations();

    handleCall(filters);

    window.addEventListener('scroll', onScroll); 

    return () => {
      window.removeEventListener('scroll', onScroll);
    }
  }, []);

  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { resultsRef.current = results; }, [results]);
  useEffect(() => { totalOrderCountRef.current = totalOrderCount; }, [totalOrderCount]);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { sortRef.current = sort; }, [sort]);

  const onScroll = debounce(() => {
    const rootHeight = root.scrollHeight ? root.scrollHeight : root.offsetHeight;
    if (root && window.innerHeight + window.scrollY + scrollPixels >= rootHeight && !loadingRef?.current && !loadingMoreRef?.current) {
      if (resultsRef?.current?.length < totalOrderCountRef?.current) {
        const newPage = currentPageRef.current + 1;

        setCurrentPage(newPage);

        handleCall(filtersRef.current, newPage, sortRef.current);
      }
    }
  }, debounceTime);

  const handleFilterChange = (filters: ICondoWeeksFilters) => {
    setResults([]);
    setCurrentPage(1);

    const filterParams = ProcessFilterParams(filters as ICondoWeeksFilters);
    const filteredFilters = { ...filters, applied: filterParams.filterApplied } as ICondoWeeksFilters;
    setFilters(filteredFilters);

    handleCall(filteredFilters, 1, sort);
  }

  const handleClearFilter = () => {
    setResults([]);
    setCurrentPage(1);

    const filteredFilters = { ...INITIAL_FITLERS };
    setFilters(filteredFilters);

    handleCall(filteredFilters, 1, sort);
  }

  const handleSortChange = (value: string | undefined) => {
    let nextSort: ISortState | null = null;
    if (value) {
      const idx = value.lastIndexOf('_');
      const orderBy = value.substring(0, idx);
      const sortOrder = value.substring(idx + 1) as 'asc' | 'desc';
      nextSort = { orderBy, sortOrder };
    }

    setSort(nextSort);
    setResults([]);
    setCurrentPage(1);

    handleCall(filters, 1, nextSort);
  }

  const handleCall = async (filters?: ICondoWeeksFilters, currentPage = 1, sortState?: ISortState | null) => {
    const isFirstPage = currentPage === 1;  
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(false);

    //const filterParams = ProcessFilterParams(filters as ICondoWeeksFilters);
    //const url = `${Urls.MarketingWeeks}?page=${currentPage}&results=${PAGE}${(isWeeks && isEmpty(queryHash)) ? '' : `&${QUERY_PARAM}=${queryHash}`}${filterParams.params}`;

    const filterParams = ProcessFilterParamsObject(filters as ICondoWeeksFilters);
    const paramsObject = filterParams?.params ? { ...filterParams.params } : {};

    const activeSort = sortState !== undefined ? sortState : sortRef.current;
    if (activeSort) {
      paramsObject.orderBy = activeSort.orderBy;
      paramsObject.sortOrder = activeSort.sortOrder;
    }

    try {
      //const resp = await axiosInstance.get(url);
      const resp = await axiosInstance.post(Urls.MarketingWeeks, {
        page: currentPage,
        results: PAGE,
        queryHash: queryHash,
        ...paramsObject
      });
      handleResponse(resp, isFirstPage);
    } catch (err) {
        console.error(err);

        setError(true);

        if (!isWeeks) {
          history.push(Routes.BasicError);
        }

        setResults([]);
        setCurrentPage(1);    
    } finally {
      setLoading(false);
      setLoadingMore(false);
    };
  }

  const handleResponse = (resp: any, isFirstPage: boolean) => {
    const responseResults = resp.data?.items?.length ? resp.data.items : [];

    setResults(isFirstPage ? responseResults : [...resultsRef.current, ...responseResults]);
    setTotalOrderCount(resp.data?.totalResults);
  }

  const handleLocations = () => {
    const url = `${Urls.MarketingWeeksLocations}${(isWeeks && isEmpty(queryHash)) ? '' : `?${QUERY_PARAM}=${queryHash}`}`;
    axiosInstance.get(url)
    .then((resp) => {
      const locations: any = resp.data ? [...resp.data] : [];
      locations.sort((a: any, b: any) => a.city?.toLowerCase().localeCompare(b.city?.toLowerCase()));
      setLocations(locations);
    }).catch((err) => {
      console.error(err);
    }).finally(() => {
    });
  }

  const filtersApplied = filters?.applied;

  if (isWeeks && !loading && (error || (!results?.length && !filtersApplied))) {
    return null;
  }

  return (
    <div className="condo-weeks-page">
      
      {!isWeeks && (
        <div className="py-2 container-fluid dark-overlay" style={{ backgroundImage: `url(${CondoWeeks})`, backgroundPosition: 'center left', backgroundSize: 'cover' }}>
          <div className="row">
            <div className="col-md-6 mx-auto text-center pt-6 pb-4">
              <h1 className="text-white"><FormattedMessage id="marketing.weeks.title.2" /></h1>
              <p className="lead text-white mb-0"><FormattedMessage id="marketing.weeks.subtitle.2" /></p>
            </div>
          </div>
        </div>)}

      <div className={`condo-weeks-page__content ${loading ? 'condo-weeks-page-loading-container' : ''}`}>
        <div className="container-fluid">
          {!loading ? (
            <>

              {(results?.length || filtersApplied) ? (
                <div className="row">
                  <div className="col-lg-12" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '35px' }}>
                    <CondoWeeksFilters
                      filters={filters}
                      locations={locations}
                      filtersApplied={!!filtersApplied}
                      onFiltersChange={handleFilterChange}
                      onClearFilters={handleClearFilter}
                    />
                  </div>
                </div>) : null}

              <div className="row condo-weeks-page__results">
                {(!isWeeks && (results?.length || filtersApplied)) ? (
                  <div className="col-lg-12 condo-weeks-page__display-row">
                    <div className="condo-weeks-page__display-message">
                      <FormattedMessage
                        id={isWeeks ? 'marketing.weeks.displaying.weeks' : 'marketing.weeks.displaying.condos'}
                        values={{ count: results?.length, total: totalOrderCount }}
                      />
                    </div>
                    <Select
                      className="condo-weeks-page__sort"
                      placeholder="Please select a sort"
                      style={{ width: 240 }}
                      allowClear
                      value={sort ? `${sort.orderBy}_${sort.sortOrder}` : undefined}
                      onChange={handleSortChange}
                      options={SORT_OPTIONS.map(o => ({ key: o.value, value: o.value, label: o.label }))}
                    />
                  </div>) : null}

                {results?.map((c, i) => (
                  <div className="col-lg-4" key={`index_card_${i}`} style={{ display: 'flex', justifyContent: 'center' }}>
                    <CondoWeeksCard
                      isWeeks={isWeeks as boolean}
                      isAccount={isAccount as boolean}
                      condo={c} redirect={redirect as string}
                      isMarketing={isMarketing}
                    />
                  </div>))}

                {!results?.length ? (
                  <CondoWeeksEmpty filtersApplied={filtersApplied} />) : null}
              </div>
            </>) : null}

          {(loading || loadingMore) ? (
            <div className="row condo-weeks-page__results">
              <div className="col-lg-4" style={{ display: 'flex', justifyContent: 'center' }}>
                <CondoWeeksCardSkeleton displayButton={isWeeks || isAccount} />
              </div>
              <div className="col-lg-4" style={{ display: 'flex', justifyContent: 'center' }}>
                <CondoWeeksCardSkeleton displayButton={isWeeks || isAccount} />
              </div>
              <div className="col-lg-4" style={{ display: 'flex', justifyContent: 'center' }}>
                <CondoWeeksCardSkeleton displayButton={isWeeks || isAccount} />
              </div>
            </div>) : null}
        </div>
      </div>
    </div>
  );
}
